import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import BadgeTradeDirection from 'src/components/Badge/BadgeTradeDirection';
import BadgeTradePointType from 'src/components/Badge/BadgeTradePointType';
import BadgeTradeRuleState from 'src/components/Badge/BadgeTradeRuleState';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import Loading from 'src/components/Loading';
import { useTradeRuleCounterParty } from 'src/hooks/useTradeRuleCounterParty';
import getNodesFromEdges from 'src/util/graphql';
import { useIntlCurrency } from 'src/util/i18n/currency';
import getUserDisplayName from 'src/util/i18n/decorators/getUserDisplayName';
import buildTimeRangeSummary from 'src/util/i18n/timeRange';

import UUID from 'src/components/UUID';
import { valuePerUnitWithStrong } from 'src/util/i18n/energyPrice';
import AdminMeterTradeRuleTableItemClause from '../AdminMeterTradeRulesTableItemClause';

const AdminMeterShowTradeRuleFragment = graphql`
  fragment AdminMeterShowTradeRule_rule on TradeRule {
    id
    buyer {
      user { id email givenName familyName }
      community { id title }
      residualId
      tradePoint { id type }
    }
    seller {
      user { id email givenName familyName }
      community { id title }
      residualId
      tradePoint { id type }
    }
    clauses {
      edges {
        node {
          id
          price
          ignoreDaylightSavings
          ignorePublicHolidays
          timezone
          ...AdminMeterTradeRulesTableItemClause_clause
        }
      }
    }
    tradeType
    state
    start
    finish
    proposedAt
    proposedBy { id email givenName familyName }
    acceptedAt
    acceptedBy { id email givenName familyName }
    rejectedAt
    rejectedBy { id email givenName familyName }
    cancelledAt
    cancelledBy { id email givenName familyName }
    closedAt
    closedBy { id email givenName familyName }
  }
`;

/**
 * @param {object} props
 * @returns {React.ReactComponentElement} - AdminMeterShowTradeRule component
 */
function AdminMeterShowTradeRule(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }

  let { rule } = props;
  rule = useFragment(AdminMeterShowTradeRuleFragment, rule);

  const { propertyId, tradePointId } = props;
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  const {
    id, tradeType, state, start, finish,
  } = rule;
  const active = { start, finish };
  const clauses = getNodesFromEdges(rule.clauses).sort((a, b) => (b.price - a.price));

  const { direction, trader, tradePoint } = useTradeRuleCounterParty(rule, tradePointId) || {};

  const userDisplayName = getUserDisplayName(intl, trader);

  return (
    <tr key={`trade-rule-${id}`}>
      <th>
        <Link to={`/properties/${propertyId}/trade-rules/${id}`}>
          <UUID uuid={id} />
        </Link>
        <br />
        <BadgeTradeDirection direction={direction} />
        <br />
        <BadgeTradeType type={tradeType} />
        <br />
        <BadgeTradeRuleState state={state} />
      </th>
      <td>
        {userDisplayName && (
          <>
            <strong>{userDisplayName}</strong>
            <br />
            <UUID uuid={trader.id} />
          </>
        )}
      </td>
      <td>
        {tradePoint && (
          <>
            <strong>{tradePoint.label}</strong>
            <br />
            <UUID uuid={tradePoint.tradePoint.id} />
            <br />
            <BadgeTradePointType type={tradePoint.tradePoint.type} />
          </>
        )}
      </td>
      <td>
        {clauses.length === 0 && (
          <p style={{ display: 'flex' }}>
            {valuePerUnitWithStrong(intl, intlCurrency, undefined)}
          </p>
        )}
        {clauses.length > 0 && (
        <ul className="ps-2">
          {clauses && clauses.map((clause) => (
            <AdminMeterTradeRuleTableItemClause
              clause={clause}
              key={clause.id}
              showConditions={false}
            />
          ))}
        </ul>
        )}
      </td>
      <td>
        {buildTimeRangeSummary(intl, active)}
      </td>
    </tr>
  );
}

export default AdminMeterShowTradeRule;

AdminMeterShowTradeRule.propTypes = {
  propertyId: PropTypes.string.isRequired,
  rule: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  tradePointId: PropTypes.string.isRequired,
};
